/**
 * TypeComprehensionSaveProvider
 *
 * The single, canonical "save a project/contract type" operation. From an
 * editor-shaped record (scalars + `_DisplaySettings.*` selections) it builds ONE
 * comprehension — the type + its `*TypeDefaultDate` definitions + the prototype
 * project/contract + the prototype's join rows (forms / observation types /
 * label types / labels) + placeholder dates — and pushes it so everything is
 * created/updated together (GUID-FKs resolved by the comprehension processor).
 * Then it resolves the prototype's numeric id into the type's Settings pointer,
 * prunes config the user deselected (on edit), and PUTs statuses (their ListDefault
 * NamespaceKey needs the type's numeric id, unknown until the push creates the type).
 *
 * Both the HPC type editor (the PSRS create/update wrap) and the spreadsheet
 * ImportProvider call this, so an imported type is identical to an editor-created
 * one — same prototype, dates, statuses. Registered as a fable service so any
 * provider can resolve it via `this.fable.TypeComprehensionSaveProvider`.
 *
 * Exercised only at runtime in the browser against a live API — validate end-to-end on QA.
 */

const libFableServiceProviderBase = require('fable-serviceproviderbase');
const libProjectTypeComprehensionBuilder = require('./ProjectTypeComprehensionBuilder.js');
const libContractProjectTypesSettingsAdapter = require('./ContractProjectTypesSettingsAdapter.js');

class TypeComprehensionSaveProvider extends libFableServiceProviderBase
{
	/**
	 * @param {*} pFable - The fable instance.
	 * @param {*} [pOptions] - Provider options.
	 * @param {string} [pServiceHash] - Fable service hash for this instance.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this.pict = this.fable;
		this.serviceType = 'TypeComprehensionSaveProvider';
	}

	/**
	 * The logged-in user's customer id, stamped onto the type and every comprehension
	 * child row (prototype project/contract, contract dates) so they inherit the right
	 * customer. The editor record doesn't expose IDCustomer, so without this the rows
	 * push `IDCustomer: 0`, which the API's update guard rejects with "You do not have
	 * permission to change customer assignment on records". Mirrors the import path's
	 * stamping in ContractProjectTypesImportProvider.
	 *
	 * @returns {number} the current user's customer id, or 0 if unknown.
	 */
	get _currentIDCustomer()
	{
		const tmpUser = this.fable && this.fable.AppData && this.fable.AppData.CurrentUser;
		return (tmpUser && (Number(tmpUser.IDCustomer) || Number(tmpUser.CustomerID))) || 0;
	}

	/**
	 * Mint a fresh random external GUID for a brand-new comprehension record: a prefixed
	 * base36 token, e.g. `PROTO-k3f9x1q8z7m2a0b4`.
	 *
	 * Why not a UUID: `fable.getUUID()` returns a dashed v4 UUID, which Headlight
	 * normalizes to its `0x`-hex GUID storage format. That transformation breaks the
	 * comprehension processor's GUID->FK correlation (the prototype, which references the
	 * type by its supplied GUID, then fails to link/create). A plain prefixed base36 token
	 * is stored verbatim — matching the format the rest of the system already round-trips —
	 * and the leading non-hex prefix guarantees it is never mistaken for a hex GUID.
	 *
	 * These GUIDs are opaque identity keys (nothing reconstructs them from Hash), so a
	 * random value means a Hash reused across types or customers never collides. The
	 * type→prototype link rides on the stored `Settings.Prototype*` pointers and the date
	 * children on their FK to the type/prototype, so opaque GUIDs lose nothing.
	 *
	 * @param {string} pPrefix - short entity tag (e.g. 'PT', 'PROTO', 'CT', 'CTDD').
	 * @returns {string} `{prefix}-{16-char base36 token}`.
	 */
	_newGUID(pPrefix)
	{
		const tmpToken = (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).slice(0, 16);
		return `${pPrefix || 'G'}-${tmpToken}`;
	}

	/**
	 * @returns {*} The shared HeadlightRestClient, or null when it isn't registered.
	 */
	get _typeStatusRestClient()
	{
		return (this.fable && this.fable.HeadlightRestClient)
			|| (this.pict && this.pict.fable && this.pict.fable.HeadlightRestClient)
			|| (this.pict && this.pict.HeadlightRestClient)
			|| null;
	}

	/**
	 * The primary-key field name for a type recordset.
	 *
	 * @param {string} pEntity - 'ProjectType' | 'ContractType'
	 * @returns {string}
	 */
	_typeStatusIDField(pEntity)
	{
		return (pEntity === 'ContractType') ? 'IDContractType' : 'IDProjectType';
	}

	/** @returns {*} the ProjectCloningAdapter fable service (its loadComprehension pushes a comprehension). */
	get _projectCloningAdapterService()
	{
		if (this.fable && this.fable.ProjectCloningAdapter)
		{
			return this.fable.ProjectCloningAdapter;
		}
		if (this.fable && this.fable.serviceManager)
		{
			return this.fable.serviceManager.instantiateServiceProvider('ProjectCloningAdapter', {});
		}
		return null;
	}

	/** Promisified loadComprehension (integrate/upsert) push. */
	_pushTypeComprehension(pComprehension)
	{
		const tmpAdapter = this._projectCloningAdapterService;
		return new Promise((resolve, reject) =>
		{
			if (!tmpAdapter || typeof tmpAdapter.loadComprehension !== 'function')
			{
				return reject(new Error('ProjectCloningAdapter.loadComprehension is unavailable — cannot push the type comprehension.'));
			}
			tmpAdapter.loadComprehension(pComprehension, (pError) => pError ? reject(pError) : resolve());
		});
	}

	/** Read the first entity matching a single-field equals filter, or null. */
	_readEntityByFilter(pEntity, pField, pValue)
	{
		const tmpClient = this._typeStatusRestClient;
		return new Promise((resolve) =>
		{
			if (!tmpClient) return resolve(null);
			tmpClient.getEntitySet(pEntity, `FBV~${pField}~EQ~${pValue}`, (pError, pRecords) =>
			{
				if (pError || !Array.isArray(pRecords) || pRecords.length === 0) return resolve(null);
				return resolve(pRecords[0]);
			}, 0, 50);
		});
	}

	/**
	 * Snapshot the status textareas off `_DisplaySettings` so they can be
	 * PUT to /Statuses after the save.
	 *
	 * @param {Object} pRecord
	 * @returns {{AvailableStatusList: string, StatusesRequiringChangeOrder: string} | null}
	 */
	_captureTypeStatusDisplay(pRecord)
	{
		const tmpDisplay = pRecord && pRecord._DisplaySettings;
		if (!tmpDisplay)
		{
			return null;
		}
		return {
			AvailableStatusList: tmpDisplay.AvailableStatusList,
			StatusesRequiringChangeOrder: tmpDisplay.StatusesRequiringChangeOrder,
		};
	}

	/**
	 * PUT the captured status text to /1.0/{Entity}/:id/Statuses after a type
	 * create/update. Failures are logged, not thrown.
	 *
	 * @param {string} pEntity
	 * @param {Object} pSavedRecord
	 * @param {Object} pNormalizedRecord
	 * @param {{AvailableStatusList: string, StatusesRequiringChangeOrder: string} | null} pStatusDisplay
	 * @returns {Promise<void>}
	 */
	async _persistTypeStatuses(pEntity, pSavedRecord, pNormalizedRecord, pStatusDisplay)
	{
		if (!pStatusDisplay)
		{
			return;
		}
		const tmpClient = this._typeStatusRestClient;
		if (!tmpClient)
		{
			this.pict.log.warn(`HeadlightRestClient unavailable — cannot persist ${pEntity} statuses.`);
			return;
		}
		const tmpIDField = this._typeStatusIDField(pEntity);
		const tmpID = (pSavedRecord && (pSavedRecord[tmpIDField] || (pSavedRecord.Record && pSavedRecord.Record[tmpIDField])))
			|| (pNormalizedRecord && pNormalizedRecord[tmpIDField]);
		if (!tmpID)
		{
			this.pict.log.warn(`Saved ${pEntity} has no ${tmpIDField} — cannot persist statuses.`);
			return;
		}
		const tmpPayload = libContractProjectTypesSettingsAdapter.statusDisplayToPayload(pStatusDisplay);
		await new Promise((resolve) =>
		{
			tmpClient.putJSON(`${pEntity}/${tmpID}/Statuses`, tmpPayload, (pError) =>
			{
				if (pError)
				{
					this.pict.log.error(`Failed to persist ${pEntity} statuses for ID ${tmpID}.`, { Error: pError.message });
				}
				return resolve();
			});
		});
	}

	/** Parse a SettingsJSON string into an object (tolerant). */
	_parseSettingsJSON(pSettingsJSON)
	{
		if (typeof pSettingsJSON !== 'string' || pSettingsJSON.length === 0) return {};
		try { const tmpParsed = JSON.parse(pSettingsJSON); return (tmpParsed && typeof tmpParsed === 'object') ? tmpParsed : {}; }
		catch (pError) { return {}; }
	}

	/** Update the saved type's Settings with the resolved numeric prototype pointer. */
	async _writePrototypePointer(pSavedType, pIDProject, pProtoGUID)
	{
		const tmpClient = this._typeStatusRestClient;
		if (!tmpClient) return;
		const tmpSettings = (pSavedType.Settings && typeof pSavedType.Settings === 'object')
			? pSavedType.Settings : this._parseSettingsJSON(pSavedType.SettingsJSON);
		tmpSettings.PrototypeIDProject = pIDProject;
		tmpSettings.PrototypeGUIDProject = pProtoGUID;
		await new Promise((resolve) =>
		{
			tmpClient.upsertEntity('ProjectType', { IDProjectType: pSavedType.IDProjectType, Settings: tmpSettings },
				(pError) =>
				{
					if (pError) this.pict.log.error(`Failed to write prototype pointer onto ProjectType ${pSavedType.IDProjectType}.`, { Error: pError.message });
					return resolve();
				});
		});
	}

	/** Update the saved contract type's Settings with the resolved numeric prototype pointer. */
	async _writeContractPrototypePointer(pSavedType, pIDContract, pProtoGUID)
	{
		const tmpClient = this._typeStatusRestClient;
		if (!tmpClient) return;
		const tmpSettings = (pSavedType.Settings && typeof pSavedType.Settings === 'object')
			? pSavedType.Settings : this._parseSettingsJSON(pSavedType.SettingsJSON);
		tmpSettings.PrototypeIDContract = pIDContract;
		tmpSettings.PrototypeGUIDContract = pProtoGUID;
		await new Promise((resolve) =>
		{
			tmpClient.upsertEntity('ContractType', { IDContractType: pSavedType.IDContractType, Settings: tmpSettings },
				(pError) =>
				{
					if (pError) this.pict.log.error(`Failed to write prototype pointer onto ContractType ${pSavedType.IDContractType}.`, { Error: pError.message });
					return resolve();
				});
		});
	}

	/** Read all rows of an entity matching pIDField=pIDValue (single high-cap page). */
	_readAllEntities(pEntity, pIDField, pIDValue)
	{
		const tmpClient = this._typeStatusRestClient;
		return new Promise((resolve) =>
		{
			if (!tmpClient || !pIDValue) return resolve([]);
			tmpClient.getEntitySet(pEntity, `FBV~${pIDField}~EQ~${pIDValue}`, (pError, pRecords) => resolve((!pError && Array.isArray(pRecords)) ? pRecords : []), 0, 200000);
		});
	}

	/** Promisified create-or-update (the rest client upserts by the PK present in the record). */
	_upsertEntity(pEntity, pRecord)
	{
		const tmpClient = this._typeStatusRestClient;
		return new Promise((resolve) =>
		{
			if (!tmpClient) return resolve();
			tmpClient.upsertEntity(pEntity, pRecord, (pError) =>
			{
				if (pError) this.pict.log.error(`Failed to upsert ${pEntity}.`, { Error: pError.message });
				return resolve();
			});
		});
	}

	/** Promisified delete by numeric id. */
	_deleteEntity(pEntity, pID)
	{
		const tmpClient = this._typeStatusRestClient;
		return new Promise((resolve) =>
		{
			if (!tmpClient || !pID) return resolve();
			tmpClient.deleteEntity(pEntity, pID, (pError) =>
			{
				if (pError) this.pict.log.warn(`Failed to delete ${pEntity} ${pID}.`, { Error: pError.message });
				return resolve();
			});
		});
	}

	/**
	 * Reconcile a type's default-date list (and the prototype's matching dates) with the
	 * editor grid by Hash — the date milestone's natural key. Existing rows are upserted in
	 * place (their ids/GUIDs survive) and brand-new rows are created — no delete-and-recreate
	 * churn. The type's own definition list deletes rows the user removed from the grid; the
	 * prototype's dates are only added/updated, NEVER deleted (a user configures the prototype
	 * separately and may keep extra dates there).
	 *
	 * @param {string} pEntity - 'ProjectType' | 'ContractType'
	 * @param {Object} pSavedType
	 * @param {Object} pPrototype
	 * @param {Array<{Hash?:string,Name?:string}>} pGridDates - the editor's current date list.
	 * @returns {Promise<void>}
	 */
	async _reconcileTypeDefaultDates(pEntity, pSavedType, pPrototype, pGridDates)
	{
		if (!this._typeStatusRestClient || !pSavedType || !pPrototype) return;
		const tmpIsContract = (pEntity === 'ContractType');
		const tmpKeys = {
			typeDateEntity: tmpIsContract ? 'ContractTypeDefaultDate' : 'ProjectTypeDefaultDate',
			protoDateEntity: tmpIsContract ? 'ContractDate' : 'ProjectDate',
			typeIDField: tmpIsContract ? 'IDContractType' : 'IDProjectType',
			protoIDField: tmpIsContract ? 'IDContract' : 'IDProject',
			typeID: pSavedType[tmpIsContract ? 'IDContractType' : 'IDProjectType'],
			protoID: pPrototype[tmpIsContract ? 'IDContract' : 'IDProject'],
			idCustomer: pSavedType.IDCustomer || this._currentIDCustomer,
		};
		if (!tmpKeys.typeID || !tmpKeys.protoID) return;

		const tmpExistingType = await this._readAllEntities(tmpKeys.typeDateEntity, tmpKeys.typeIDField, tmpKeys.typeID);
		const tmpExistingProto = await this._readAllEntities(tmpKeys.protoDateEntity, tmpKeys.protoIDField, tmpKeys.protoID);
		const tmpPlan = libProjectTypeComprehensionBuilder.computeDefaultDateReconciliation(tmpExistingType, tmpExistingProto, pGridDates, tmpKeys);

		for (const tmpRow of tmpPlan.typeUpserts) await this._upsertEntity(tmpKeys.typeDateEntity, tmpRow);
		for (const tmpRow of tmpPlan.protoUpserts) await this._upsertEntity(tmpKeys.protoDateEntity, tmpRow);
		for (const tmpID of tmpPlan.typeDeletes) await this._deleteEntity(tmpKeys.typeDateEntity, tmpID);
	}

	/**
	 * Build + push the project-type comprehension, resolve the prototype's IDProject
	 * into Settings.PrototypeIDProject, prune deselected joins on edit, and PUT statuses.
	 *
	 * @param {Object} pRecord - the editor record (with _DisplaySettings selections).
	 * @param {boolean} pIsCreate
	 * @returns {Promise<Object>} the saved ProjectType record (for PSRS).
	 */
	async saveProjectType(pRecord, pIsCreate)
	{
		const tmpDisplay = (pRecord && pRecord._DisplaySettings) || {};
		const tmpHash = pRecord.Hash || libProjectTypeComprehensionBuilder.slug(pRecord.Name || `pt-${Date.now()}`);
		const tmpExistingSettings = (pRecord.Settings && typeof pRecord.Settings === 'object') ? pRecord.Settings : this._parseSettingsJSON(pRecord.SettingsJSON);

		// External GUIDs are opaque identity keys: reuse the stored record/prototype GUIDs
		// on edit; mint fresh random UUIDs on create so a Hash reused across types or
		// customers never collides (no longer derived from Hash).
		const tmpTypeGUID = (!pIsCreate && pRecord.GUIDProjectType) ? pRecord.GUIDProjectType : this._newGUID('PT');
		const tmpProtoGUID = (tmpExistingSettings && tmpExistingSettings.PrototypeGUIDProject) || this._newGUID('PROTO');

		// Capture statuses (PUT separately to the /Statuses endpoint after the save).
		const tmpStatusDisplay = this._captureTypeStatusDisplay(pRecord);
		const tmpDefaultDates = Array.isArray(tmpDisplay.DefaultDates) ? tmpDisplay.DefaultDates : [];

		// Type scalar fields (fromDisplay strips status display from Settings).
		const tmpNormalized = libContractProjectTypesSettingsAdapter.fromDisplay(JSON.parse(JSON.stringify(pRecord)));
		const tmpTypeFields = {
			Name: pRecord.Name || '', Hash: tmpHash, Description: pRecord.Description || '',
			Ordinal: pRecord.Ordinal || 0, IDCustomer: pRecord.IDCustomer || this._currentIDCustomer, IDProgram: pRecord.IDProgram || 0,
			Settings: (tmpNormalized && tmpNormalized.Settings) || {},
		};

		// Build the comprehension: just the type + its prototype project shell. The
		// prototype's forms/observation-types/labels are configured through the normal
		// project tools (and cloned by the type-seeding service); the default dates are
		// reconciled separately by Hash (see _reconcileTypeDefaultDates), not authored here.
		const tmpComprehension = libProjectTypeComprehensionBuilder.buildProjectTypeComprehension(
			{
				typeGUID: tmpTypeGUID,
				prototypeGUID: tmpProtoGUID,
				type: tmpTypeFields,
			});

		await this._pushTypeComprehension(tmpComprehension);

		// Resolve the saved type + prototype by the GUIDs we just pushed (Hash may be
		// reused across types, so it can't identify the row), wire the numeric prototype
		// pointer into the type's Settings, reconcile the default dates, then PUT statuses.
		const tmpSavedType = await this._readEntityByFilter('ProjectType', 'GUIDProjectType', tmpTypeGUID);
		const tmpPrototype = await this._readEntityByFilter('Project', 'GUIDProject', tmpProtoGUID);
		if (tmpSavedType && tmpPrototype && tmpPrototype.IDProject)
		{
			await this._writePrototypePointer(tmpSavedType, tmpPrototype.IDProject, tmpProtoGUID);
			await this._reconcileTypeDefaultDates('ProjectType', tmpSavedType, tmpPrototype, tmpDefaultDates);
			await this._persistTypeStatuses('ProjectType', tmpSavedType, tmpSavedType, tmpStatusDisplay);
		}
		return tmpSavedType || pRecord;
	}

	/**
	 * Contract-type analogue of saveProjectType. Contracts carry no
	 * form/observation/label joins — just the type, its default-date definitions, the
	 * prototype contract, and the prototype's placeholder ContractDate rows.
	 *
	 * @param {Object} pRecord
	 * @param {boolean} pIsCreate
	 * @returns {Promise<Object>} the saved ContractType record (for PSRS).
	 */
	async saveContractType(pRecord, pIsCreate)
	{
		const tmpDisplay = (pRecord && pRecord._DisplaySettings) || {};
		const tmpHash = pRecord.Hash || libProjectTypeComprehensionBuilder.slug(pRecord.Name || `ct-${Date.now()}`);
		const tmpExistingSettings = (pRecord.Settings && typeof pRecord.Settings === 'object') ? pRecord.Settings : this._parseSettingsJSON(pRecord.SettingsJSON);
		// Opaque GUIDs: reuse the stored ones on edit; mint fresh random UUIDs on create
		// so a reused Hash never collides across types/customers (not derived from Hash).
		const tmpTypeGUID = (!pIsCreate && pRecord.GUIDContractType) ? pRecord.GUIDContractType : this._newGUID('CT');
		const tmpProtoGUID = (tmpExistingSettings && tmpExistingSettings.PrototypeGUIDContract) || this._newGUID('PROTOC');

		const tmpStatusDisplay = this._captureTypeStatusDisplay(pRecord);
		const tmpAllowedProjectTypesDisplay = tmpDisplay.AllowedProjectTypeIDs;
		const tmpDefaultDates = Array.isArray(tmpDisplay.DefaultDates) ? tmpDisplay.DefaultDates : [];
		const tmpNormalized = libContractProjectTypesSettingsAdapter.fromDisplay(JSON.parse(JSON.stringify(pRecord)));
		const tmpTypeFields = {
			Name: pRecord.Name || '', Hash: tmpHash, Description: pRecord.Description || '',
			IDCustomer: pRecord.IDCustomer || this._currentIDCustomer, Settings: (tmpNormalized && tmpNormalized.Settings) || {},
		};

		// Just the type + its prototype contract shell; the default dates are reconciled
		// separately by Hash (see _reconcileTypeDefaultDates), not authored in the comprehension.
		const tmpComprehension = libProjectTypeComprehensionBuilder.buildContractTypeComprehension(
			{ typeGUID: tmpTypeGUID, prototypeGUID: tmpProtoGUID, type: tmpTypeFields });

		await this._pushTypeComprehension(tmpComprehension);

		// Resolve the saved type by the GUID we pushed (Hash may be reused, so it can't
		// identify the row); the prototype is already resolved by its GUID.
		const tmpSavedType = await this._readEntityByFilter('ContractType', 'GUIDContractType', tmpTypeGUID);
		const tmpPrototype = await this._readEntityByFilter('Contract', 'GUIDContract', tmpProtoGUID);
		if (tmpSavedType && tmpPrototype && tmpPrototype.IDContract)
		{
			await this._writeContractPrototypePointer(tmpSavedType, tmpPrototype.IDContract, tmpProtoGUID);
			await this._reconcileTypeDefaultDates('ContractType', tmpSavedType, tmpPrototype, tmpDefaultDates);
			await this._persistTypeStatuses('ContractType', tmpSavedType, tmpSavedType, tmpStatusDisplay);
			await this._persistContractTypeAllowedProjectTypes(tmpSavedType, tmpAllowedProjectTypesDisplay);
		}
		return tmpSavedType || pRecord;
	}

	/**
	 * Resolve the editor's selected allowed-project-type values to numeric
	 * IDProjectType strings and PUT them to
	 * `/1.0/ContractType/:id/AllowedProjectTypes` (ListDefault-backed). The
	 * endpoint stores by ID — sending IDs avoids the Hash → ID lookup at the
	 * boundary that (a) required a second server round-trip for the catalog
	 * and (b) picked the wrong ID when a customer had duplicate ProjectType
	 * Hashes. Failures are logged, not thrown.
	 *
	 * @param {Object} pSavedType - the saved ContractType (for IDContractType).
	 * @param {string | undefined} pAllowedDisplay - comma-joined selected values from the editor.
	 * @returns {Promise<void>}
	 */
	async _persistContractTypeAllowedProjectTypes(pSavedType, pAllowedDisplay)
	{
		// Field absent from the editor record → leave the existing allowed list untouched.
		if (pAllowedDisplay === undefined || pAllowedDisplay === null)
		{
			return;
		}
		const tmpClient = this._typeStatusRestClient;
		const tmpID = pSavedType && pSavedType.IDContractType;
		if (!tmpClient || !tmpID)
		{
			if (!tmpClient) this.pict.log.warn('HeadlightRestClient unavailable — cannot persist ContractType allowed project types.');
			return;
		}
		const tmpSelected = libContractProjectTypesSettingsAdapter.allowedProjectTypesDisplayToPayload({ AllowedProjectTypeIDs: pAllowedDisplay });
		const tmpIDs = await this._normalizeToProjectTypeIDs(tmpSelected);
		// Wrap in { IDs: [...] } rather than PUTting the bare array: the endpoint's
		// restify bodyParser runs with mapParams:true and rejects a top-level array
		// ("Cannot map POST body ... onto req.params"). An object body maps cleanly.
		await new Promise((resolve) =>
		{
			tmpClient.putJSON(`ContractType/${tmpID}/AllowedProjectTypes`, { IDs: tmpIDs }, (pError) =>
			{
				if (pError) this.pict.log.error(`Failed to persist ContractType allowed project types for ID ${tmpID}.`, { Error: pError.message });
				return resolve();
			});
		});
	}

	/**
	 * Map selected values (typically ProjectType Hashes emitted by the picker,
	 * or a stray numeric IDProjectType) to canonical numeric IDProjectType
	 * strings; unknown values are dropped. Fetches the customer's ProjectTypes
	 * once to build the lookup.
	 *
	 * Duplicate Hashes (which would map to more than one IDProjectType) are
	 * intentionally dropped rather than silently binding to whichever record
	 * the server catalog returned last. If the ProjectType catalog fails to
	 * load, we return an empty list rather than pass the raw picker values
	 * through: the endpoint would drop them anyway (they aren't IDs), and
	 * an empty list is preferable to a silent partial write of nothing.
	 *
	 * @param {Array<string>} pValues
	 * @returns {Promise<Array<string>>} decimal ID strings
	 */
	async _normalizeToProjectTypeIDs(pValues)
	{
		const tmpValues = Array.isArray(pValues) ? pValues : [];
		if (tmpValues.length === 0)
		{
			return [];
		}
		const tmpClient = this._typeStatusRestClient;
		const tmpTypes = await new Promise((resolve) =>
		{
			if (!tmpClient) return resolve([]);
			// Scope to this customer + a real page size. A bare `ProjectTypes` relies on the server's default row cap
			// (~250) and can silently omit rows — dropping the caller's selections during this Hash→ID resolution on a
			// write path. Project types are a bounded per-customer catalog, so one generous page covers them.
			const tmpTypesURL = this._currentIDCustomer
				? `ProjectTypes/FilteredTo/FBV~IDCustomer~EQ~${this._currentIDCustomer}/0/5000`
				: 'ProjectTypes/0/5000';
			tmpClient.getJSON(tmpTypesURL, (pError, pBody) => resolve((!pError && Array.isArray(pBody)) ? pBody : []));
		});
		if (tmpTypes.length === 0)
		{
			return [];
		}
		// Build TWO indexes:
		//   idBySelfID  — direct match on "3" (rare, only when the caller
		//                 passed an already-resolved ID)
		//   idByHash    — Hash → { ID, Count }; Count > 1 means ambiguous
		const tmpIDBySelfID = new Set();
		const tmpIDByHash = {};
		for (const tmpType of tmpTypes)
		{
			if (!tmpType || tmpType.IDProjectType == null) continue;
			tmpIDBySelfID.add(String(tmpType.IDProjectType));
			if (typeof tmpType.Hash === 'string' && tmpType.Hash.length > 0)
			{
				if (!tmpIDByHash[tmpType.Hash])
				{
					tmpIDByHash[tmpType.Hash] = { ID: String(tmpType.IDProjectType), Count: 1 };
				}
				else
				{
					tmpIDByHash[tmpType.Hash].Count++;
				}
			}
		}
		const tmpSeen = new Set();
		const tmpResult = [];
		for (const tmpRaw of tmpValues)
		{
			const tmpStr = String(tmpRaw).trim();
			let tmpID = null;
			if (tmpIDBySelfID.has(tmpStr))
			{
				tmpID = tmpStr;
			}
			else
			{
				const tmpMatch = tmpIDByHash[tmpStr];
				if (tmpMatch && tmpMatch.Count === 1) tmpID = tmpMatch.ID;
			}
			if (tmpID && !tmpSeen.has(tmpID))
			{
				tmpSeen.add(tmpID);
				tmpResult.push(tmpID);
			}
		}
		return tmpResult;
	}
}

TypeComprehensionSaveProvider.default_configuration = {
	Name: 'TypeComprehensionSaveProvider',
	ProviderType: 'TypeComprehensionSaveProvider',
};

module.exports = TypeComprehensionSaveProvider;
